const getDocumentStylesMarkup = () =>
  Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((node) => node.outerHTML)
    .join('\n');

const normalizeSourceNodes = (source) => {
  if (!source) return [];

  if (typeof source === 'string') {
    return Array.from(document.querySelectorAll(source));
  }

  if (Array.isArray(source)) {
    return source.filter((item) => item instanceof Element);
  }

  if (source instanceof Element) {
    return [source];
  }

  return [];
};

const isElementVisible = (element) => {
  if (!(element instanceof Element)) return false;
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  return element.getClientRects().length > 0;
};

const syncFormControlValues = (sourceNode, cloneNode) => {
  const sourceFields = sourceNode.querySelectorAll('input, textarea, select');
  const cloneFields = cloneNode.querySelectorAll('input, textarea, select');

  sourceFields.forEach((sourceField, index) => {
    const cloneField = cloneFields[index];
    if (!cloneField) return;

    if (sourceField instanceof HTMLInputElement && cloneField instanceof HTMLInputElement) {
      if (sourceField.type === 'checkbox' || sourceField.type === 'radio') {
        cloneField.checked = sourceField.checked;
        if (sourceField.checked) cloneField.setAttribute('checked', 'checked');
        else cloneField.removeAttribute('checked');
      } else if (sourceField.type !== 'file') {
        cloneField.value = sourceField.value;
        cloneField.setAttribute('value', sourceField.value);
      }
      return;
    }

    if (sourceField instanceof HTMLTextAreaElement && cloneField instanceof HTMLTextAreaElement) {
      cloneField.value = sourceField.value;
      cloneField.textContent = sourceField.value;
      return;
    }

    if (sourceField instanceof HTMLSelectElement && cloneField instanceof HTMLSelectElement) {
      cloneField.value = sourceField.value;
      Array.from(cloneField.options).forEach((option, optionIndex) => {
        const isSelected = Boolean(sourceField.options[optionIndex]?.selected);
        option.selected = isSelected;
        if (isSelected) option.setAttribute('selected', 'selected');
        else option.removeAttribute('selected');
      });
    }
  });
};

const getVisiblePrintableNodes = () => {
  const nodes = Array.from(document.querySelectorAll('.standard-form-print-area')).filter(isElementVisible);
  return nodes.filter((node) => !nodes.some((candidate) => candidate !== node && candidate.contains(node)));
};

const getPrintableNodesFromSource = (source) => {
  const sourceNodes = normalizeSourceNodes(source);
  if (!sourceNodes.length) return [];

  const nodes = sourceNodes.flatMap((node) => {
    if (node.matches?.('.standard-form-print-area')) return [node];
    const descendants = Array.from(node.querySelectorAll('.standard-form-print-area'));
    return descendants.length ? descendants : [node];
  });

  return nodes.filter((node) => !nodes.some((candidate) => candidate !== node && candidate.contains(node)));
};

const buildPrintableMarkup = ({ title, contentMarkup, isLandscape }) => `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${title}</title>
    ${getDocumentStylesMarkup()}
    <style>
      @page {
        size: ${isLandscape ? 'landscape' : 'portrait'};
        margin: 0.35in;
      }

      html,
      body {
        width: 100%;
        min-height: 100%;
        background: #ffffff !important;
      }

      body {
        margin: 0;
        padding: 0;
        color: #0f172a;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      @media print {
        body,
        body * {
          visibility: visible !important;
        }
      }

      .standard-form-print-window {
        padding: 0.1in 0.15in 0;
      }

      .standard-form-print-window .print-hidden,
      .standard-form-print-window [data-preview-hidden="true"],
      .standard-form-print-window [data-required-indicator="true"],
      .standard-form-print-window button,
      .standard-form-print-window [type="button"] {
        display: none !important;
      }

      .standard-form-print-window .overflow-x-auto {
        overflow: visible !important;
      }

      .standard-form-print-window input:not([type="checkbox"]):not([type="radio"]),
      .standard-form-print-window select,
      .standard-form-print-window textarea {
        appearance: none !important;
        -webkit-appearance: none !important;
        border: 0 !important;
        background: transparent !important;
        box-shadow: none !important;
        outline: none !important;
        padding: 0 !important;
        margin: 0 !important;
        width: 100% !important;
        min-height: 0 !important;
        resize: none !important;
      }

      .standard-form-print-window [data-read-only-multiline="true"] {
        border: 0 !important;
        background: transparent !important;
        box-shadow: none !important;
        outline: none !important;
        padding: 0 !important;
        margin: 0 !important;
        min-height: 0 !important;
      }

      .standard-form-print-window .standard-form-print-area {
        visibility: visible !important;
        position: static !important;
        inset: auto !important;
        margin: 0 !important;
        max-height: none !important;
        overflow: visible !important;
        box-shadow: none !important;
        border: none !important;
        background: #ffffff !important;
      }
    </style>
  </head>
  <body>
    <div class="standard-form-print-window">${contentMarkup}</div>
  </body>
</html>`;

export const printVisibleStandardForm = ({
  title = document.title || 'Print',
  source,
} = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;

  const printableNodes = source ? getPrintableNodesFromSource(source) : getVisiblePrintableNodes();
  if (!printableNodes.length) {
    window.print();
    return false;
  }

  const printFrame = document.createElement('iframe');
  printFrame.setAttribute('title', `${title} print frame`);
  printFrame.setAttribute('aria-hidden', 'true');
  printFrame.style.position = 'fixed';
  printFrame.style.left = '-10000px';
  printFrame.style.top = '0';
  printFrame.style.width = '1px';
  printFrame.style.height = '1px';
  printFrame.style.border = '0';
  printFrame.style.opacity = '0';
  printFrame.style.pointerEvents = 'none';

  const cleanup = () => {
    printFrame.remove();
  };

  const printContainer = document.createElement('div');
  printableNodes.forEach((node) => {
    const clone = node.cloneNode(true);
    syncFormControlValues(node, clone);
    printContainer.appendChild(clone);
  });

  const isLandscape = printableNodes.some((node) =>
    Boolean(
      node.matches?.('[data-print-orientation="landscape"]') ||
      node.querySelector?.('[data-print-orientation="landscape"]')
    )
  );

  printFrame.onload = () => {
    const printWindow = printFrame.contentWindow;
    const printDocument = printFrame.contentDocument;

    if (!printWindow || !printDocument) {
      cleanup();
      return;
    }

    const waitForAssets = Promise.all([
      printDocument.fonts?.ready?.catch(() => undefined) ?? Promise.resolve(),
      Promise.all(
        Array.from(printDocument.images || []).map((image) => {
          if (image.complete) return Promise.resolve();
          return new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          });
        })
      ),
    ]);

    const handleAfterPrint = () => {
      printWindow.removeEventListener('afterprint', handleAfterPrint);
      cleanup();
    };

    printWindow.addEventListener('afterprint', handleAfterPrint);

    waitForAssets.finally(() => {
      window.setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 150);
    });
  };

  document.body.appendChild(printFrame);
  printFrame.srcdoc = buildPrintableMarkup({
    title,
    contentMarkup: printContainer.innerHTML,
    isLandscape,
  });

  window.setTimeout(cleanup, 60000);
  return true;
};

export default printVisibleStandardForm;
